import React, { useMemo } from 'react'
import { DcChart, DcDiv, DcFlexBox, DcText, colors } from 'pixel-base'
import { InventoryProduct } from './types'
import { InventoryStats } from './useInventoryStats'

interface SellingFasterChartProps {
  data: InventoryProduct[]
  loading: boolean
  error: string | null
  onProductClick?: (product: InventoryProduct) => void
  showGaugeOnly?: boolean
  showBarChartOnly?: boolean
  stats?: InventoryStats
  onTitleClick?: () => void
}

const SellingFasterChart: React.FC<SellingFasterChartProps> = ({
  data,
  loading,
  error,
  onProductClick,
  showGaugeOnly = false,
  showBarChartOnly = false,
  stats,
  onTitleClick,
}) => {
  // Use stats data for velocity or calculate from data as fallback
  const velocityStats = useMemo(() => {
    // Use stats data if available
    if (stats?.velocity_performance) {
      return {
        average: stats.velocity_performance.metrics.avg_daily_velocity,
        max: stats.velocity_performance.metrics.avg_daily_velocity * 2, // Estimate max
        min: 0,
        score: stats.velocity_performance.score,
        status: stats.velocity_performance.status,
      }
    }

    // Fallback to calculating from data
    if (!data || data.length === 0) {
      return { average: 0, max: 0, min: 0, score: 0, status: 'unknown' }
    }

    const velocities = data.map((p) => p.daily_velocity || 0)
    const average =
      velocities.reduce((sum, v) => sum + v, 0) / velocities.length
    const max = Math.max(...velocities)
    const min = Math.min(...velocities)

    return { average, max, min, score: average * 20, status: 'good' }
  }, [data, stats])

  // Velocity gauge data
  const gaugeData = useMemo(() => {
    // Use score from stats if available, otherwise use average velocity
    const value = velocityStats.score || velocityStats.average
    let color = colors.success
    let status = velocityStats.status || 'Excellent'

    // Determine color based on status or score
    if (status === 'critical' || value < 30) {
      color = colors.danger
      status = 'Critical'
    } else if (status === 'warning' || value < 60) {
      color = colors.warning
      status = 'Warning'
    } else if (status === 'good' || value < 80) {
      color = colors.primary
      status = 'Good'
    } else {
      color = colors.success
      status = 'Excellent'
    }

    return {
      series: [
        {
          name: 'Velocity Performance',
          type: 'gauge',
          min: 0,
          max: 100,
          splitNumber: 5,
          radius: '90%',
          axisLine: {
            lineStyle: {
              width: 10,
              color: [
                [0.2, colors.danger],
                [0.4, colors.warning],
                [0.6, colors.info],
                [0.8, colors.success],
                [1, colors.success],
              ],
            },
          },
          pointer: {
            itemStyle: {
              color: color,
            },
          },
          axisTick: {
            distance: -10,
            length: 10,
            lineStyle: {
              color: '#fff',
              width: 2,
            },
          },
          splitLine: {
            distance: -10,
            length: 10,
            lineStyle: {
              color: '#fff',
              width: 4,
            },
          },
          axisLabel: {
            color: '#fff',
            distance: -25,
            fontSize: 12,
          },
          detail: {
            valueAnimation: true,
            formatter: '{value}%',
            color: color,
            fontSize: 24,
            fontWeight: 'bold',
          },
          data: [
            {
              value: value,
              name: 'Velocity Performance',
            },
          ],
        },
      ],
      status,
      color,
    }
  }, [velocityStats])

  // Revenue bar chart data
  const revenueData = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        yAxis: [],
        series: [
          {
            name: 'Revenue',
            type: 'bar',
            data: [],
            itemStyle: {
              color: colors.success,
            },
          },
        ],
      }
    }

    const products = data.slice(0, 10) // Show top 10 products
    const yAxis = products.map((product) => product.product_name)
    const seriesData = products.map((product) => ({
      value: product.total_revenue || 0,
      itemStyle: {
        color: colors.success,
      },
    }))

    return {
      yAxis,
      series: [
        {
          name: 'Revenue',
          type: 'bar',
          data: seriesData,
          itemStyle: {
            color: colors.success,
          },
        },
      ],
    }
  }, [data])

  const gaugeOptions = useMemo(
    () => ({
      tooltip: {
        formatter: (params: any) => `
          <div style="padding: 8px;">
            <strong>Velocity Performance</strong><br/>
            Average: ${params.value.toFixed(2)}<br/>
            Status: ${gaugeData.status}<br/>
            Max: ${velocityStats.max.toFixed(2)}<br/>
            Min: ${velocityStats.min.toFixed(2)}
          </div>
        `,
      },
      series: gaugeData.series,
    }),
    [gaugeData, velocityStats],
  )

  const barOptions = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: any) => {
          const data = params[0]
          return `
            <div style="padding: 8px;">
              <strong>${data.name}</strong><br/>
              Revenue: $${data.value.toLocaleString()}<br/>
              Velocity: ${(data.value / 1000).toFixed(2)}
            </div>
          `
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => `$${(value / 1000).toFixed(0)}k`,
        },
      },
      yAxis: {
        type: 'category',
        data: revenueData.yAxis,
        axisLabel: {
          interval: 0,
          formatter: (value: string) => {
            return value.length > 20 ? value.substring(0, 20) + '...' : value
          },
        },
      },
      series: revenueData.series,
    }),
    [revenueData],
  )

  if (loading) {
    return (
      <DcDiv
        style={{
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DcText>Loading inventory data...</DcText>
      </DcDiv>
    )
  }

  if (error) {
    return (
      <DcDiv
        style={{
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DcText color={colors.danger}>Error loading data: {error}</DcText>
      </DcDiv>
    )
  }

  // Show only gauge
  if (showGaugeOnly) {
    return (
      <DcDiv>
        <DcDiv style={{ position: 'relative' }}>
          <DcChart
            type="gauge"
            data={gaugeData}
            options={gaugeOptions}
            height={300}
            width="100%"
            title="Velocity Performance"
            isLoading={loading}
            isTitleClickable={!!onTitleClick}
            onClickTitle={onTitleClick}
          />
          {/* Status indicator */}
          <DcDiv
            style={{
              position: 'absolute',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              textAlign: 'center',
            }}
          >
            <DcText
              style={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: gaugeData.color,
              }}
            >
              {gaugeData.status}
            </DcText>
          </DcDiv>
        </DcDiv>

        {/* Additional Metrics */}
        {stats?.velocity_performance && (
          <DcDiv
            style={{
              marginTop: '20px',
              padding: '16px',
              backgroundColor: colors.background,
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
            }}
          >
            <DcText
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: colors.text,
                marginBottom: '12px',
              }}
            >
              Velocity Performance Metrics
            </DcText>

            <DcDiv
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '8px',
                fontSize: '12px',
              }}
            >
              <DcDiv>
                <DcText style={{ color: colors.grayMedium }}>
                  Products with Sales
                </DcText>
                <DcText style={{ fontWeight: '600', color: colors.text }}>
                  {stats.velocity_performance.metrics.products_with_sales.toLocaleString()}
                </DcText>
              </DcDiv>
              <DcDiv>
                <DcText style={{ color: colors.grayMedium }}>
                  Avg Daily Velocity
                </DcText>
                <DcText style={{ fontWeight: '600', color: colors.text }}>
                  {stats.velocity_performance.metrics.avg_daily_velocity.toFixed(
                    1,
                  )}
                </DcText>
              </DcDiv>
              <DcDiv>
                <DcText style={{ color: colors.grayMedium }}>
                  High Velocity
                </DcText>
                <DcText style={{ fontWeight: '600', color: colors.success }}>
                  {stats.velocity_performance.metrics.high_velocity_products}
                </DcText>
              </DcDiv>
              <DcDiv>
                <DcText style={{ color: colors.grayMedium }}>
                  Low Velocity
                </DcText>
                <DcText style={{ fontWeight: '600', color: colors.warning }}>
                  {stats.velocity_performance.metrics.low_velocity_products}
                </DcText>
              </DcDiv>
            </DcDiv>
          </DcDiv>
        )}
      </DcDiv>
    )
  }

  // Show only bar chart
  if (showBarChartOnly) {
    return (
      <DcDiv>
        <DcChart
          type="horizontalBar"
          data={revenueData}
          options={barOptions}
          height={300}
          width="100%"
          isLoading={loading}
        />
      </DcDiv>
    )
  }

  // Show both (original layout)
  return (
    <DcDiv>
      <DcFlexBox gap="20px" height="auto">
        {/* Primary Chart - Velocity Gauge */}
        <DcDiv style={{ width: '40%' }}>
          <DcDiv style={{ position: 'relative' }}>
            <DcChart
              type="gauge"
              data={gaugeData}
              options={gaugeOptions}
              height={300}
              width="100%"
              title="Velocity Performance"
              isLoading={loading}
              isTitleClickable={!!onTitleClick}
              onClickTitle={onTitleClick}
            />
            {/* Status indicator */}
            <DcDiv
              style={{
                position: 'absolute',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                textAlign: 'center',
              }}
            >
              <DcText
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: gaugeData.color,
                }}
              >
                {gaugeData.status}
              </DcText>
            </DcDiv>
          </DcDiv>
        </DcDiv>

        {/* Secondary Chart - Revenue Bar Chart */}
        <DcDiv style={{ width: '60%' }}>
          <DcText
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: colors.text,
            }}
          >
            Revenue Contribution
          </DcText>
          <DcChart
            type="horizontalBar"
            data={revenueData}
            options={barOptions}
            height={300}
            width="100%"
            isLoading={loading}
          />
        </DcDiv>
      </DcFlexBox>
    </DcDiv>
  )
}

export default SellingFasterChart
